<template>
  <div class="survey-overview">
    <!-- Completion ring + key stats -->
    <div class="overview-hero">
      <ProgressRing
        :percent="Number(stats.percent)"
        :size="128"
        :color="ringColor"
        sublabel="complete"
      />
      <div class="overview-stats">
        <div class="overview-stat">
          <span class="overview-stat-value">{{ stats.total }}</span>
          <span class="overview-stat-label">Places</span>
        </div>
        <div class="overview-stat">
          <span class="overview-stat-value">{{ stats.total - stats.noResponses }}</span>
          <span class="overview-stat-label">Visited</span>
        </div>
        <div class="overview-stat">
          <span class="overview-stat-value">{{ survey.surveyors.length }}</span>
          <span class="overview-stat-label">Surveyors</span>
        </div>
      </div>
    </div>

    <!-- Response breakdown donut -->
    <div class="overview-section" v-if="stats.total > 0">
      <h4 class="overview-section-title">Response Breakdown</h4>
      <DonutChart
        :data="responseData"
        :size="150"
        center-label="places"
        layout="row"
      />
    </div>

    <!-- Meta -->
    <div class="overview-meta">
      <div class="overview-meta-row">
        <v-icon size="18" color="#90A4AE">mdi-calendar-outline</v-icon>
        <span>Created {{ formattedDate }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import _ from "lodash";

import ProgressRing from "@/components/viz/ProgressRing.vue";
import DonutChart from "@/components/viz/DonutChart.vue";
import PlaceUtil from "@/util/PlaceUtil";
import { OrgInfoStore } from "@/stores/OrgInfoStore";

const COMPONENT_NAME = "SurveyOverview";

export default {
  name: COMPONENT_NAME,
  components: { ProgressRing, DonutChart },
  data: function () {
    return { OrgInfoStore };
  },
  computed: {
    stats() {
      const total = Object.keys(this.survey.places || {}).length;
      if (total === 0) {
        return { percent: 0, noResponses: 0, total: 0 };
      }
      const noResponses = _.filter(
        this.survey.places,
        (place) => _.get(place, "responseCode.abbreviation") === "NR"
      ).length;
      return {
        percent: (((total - noResponses) / total) * 100).toFixed(1),
        noResponses,
        total,
      };
    },
    ringColor() {
      const p = Number(this.stats.percent);
      if (p >= 80) return "#2E7D32";
      if (p >= 40) return "#1565C0";
      return "#F57C00";
    },
    responseData() {
      const counts = {};
      _.forEach(this.survey.places, (place) => {
        const abbr = _.get(place, "responseCode.abbreviation", "NR");
        counts[abbr] = (counts[abbr] || 0) + 1;
      });
      return Object.keys(counts)
        .map((abbr) => ({
          label: this.getResponseLabel(abbr),
          value: counts[abbr],
          color: this.getResponseColor(abbr),
        }))
        .sort((a, b) => b.value - a.value);
    },
    formattedDate() {
      return new Date(this.survey.createTime).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
  methods: {
    getResponseColor(abbr) {
      const orgColor = _.get(OrgInfoStore.responseCodeColors, abbr);
      if (orgColor) return orgColor;
      return PlaceUtil.responseToColor(abbr);
    },
    getResponseLabel(abbr) {
      const code = _.find(
        OrgInfoStore.responseCodes,
        (c) => c.abbreviation === abbr
      );
      if (code && code.response) return code.response;
      if (abbr === "NR") return "No Response";
      return abbr;
    },
  },
  props: {
    survey: Object,
  },
};
</script>

<style scoped>
.survey-overview {
  padding: 8px 16px 16px;
}

.overview-hero {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 8px 0 20px;
}

.overview-stats {
  display: flex;
  flex: 1;
  justify-content: space-around;
  gap: 8px;
}

.overview-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.overview-stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #263238;
  line-height: 1;
}

.overview-stat-label {
  font-size: 0.75rem;
  color: #90A4AE;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px;
}

.overview-section {
  padding: 16px 0;
  border-top: 1px solid #ECEFF1;
}

.overview-section-title {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #78909C;
  margin-bottom: 16px;
}

.overview-meta {
  padding-top: 16px;
  border-top: 1px solid #ECEFF1;
}

.overview-meta-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: #607D8B;
}
</style>
